import * as React from 'react';
import { Controller, FormProvider, SubmitHandler, useForm, useFormContext } from 'react-hook-form';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { atom, useAtomValue } from 'jotai';
import { DevTool } from '@hookform/devtools';
import { useTranslation } from 'next-i18next';
import { GoogleAutoCompleteInput, GooglePlacePrediction } from '@restworld/ui-ds-base';
import {
  Checkbox,
  CheckboxGroup,
  DatePicker,
  FormLabelWrapper,
  HelperTextRElement,
  TextInput,
  TextArea,
} from '@restworld/ui-ds-base';
import { Occupation, Worker, WorkerPastExperience } from '@restworld/utility-types';
import { genericDateFormat, getLocationAddressFields, toDateFormat } from '@restworld/utils-common';

import {
  EditModalTitle,
  IEditModalProps,
  EditFieldOptionsModal,
  EditModalFooter,
  FieldOptionsViewType,
} from '../profile-edit-modal';
import { getWorkerAtom, getWorkerFieldOptionsAtom } from '../../../global-state.worker-data';
import { useApiSubmitHandler } from '../../hooks';
import { FormLabelLabel, useExperienceFieldOptions } from '../../../worker-application-wizard';

type IWorkExperienceFormContentProps = IEditModalProps;
export type ExperienceInputProps = {
  domain_interest: string;
  skills: string[];
  restaurant_type: string;
  occupation_id: string;
  city?: string;
  from_date: string;
  to_date: string;
  description: string;
  restaurant_name: string;
  currently_working: boolean;
  address: string;
  address_lat?: number;
  address_lon?: number;
  country?: string;
  postal_code?: string;
  administrative_area_level_1?: string;
  administrative_area_level_2?: string;
  gmaps_place_id?: string;
  gmaps_last_updated_at?: string;
};
const defaultValuesAtom = atom((get) => {
  const { data: workerData } = get(getWorkerAtom);
  return workerData?.past_experiences.length
    ? workerData?.past_experiences.map((v) => {
        return {
          domain_interest: v.domain_interest,
          skills: v.skills?.length ? v.skills.map((v) => v?.id?.toString()).filter(Boolean) : [],
          restaurant_type: v.restaurant_type,
          occupation_id: v.occupation_id?.toString() ?? '',
          city: v.city,
          from_date: v.from_date,
          to_date: v.to_date,
          description: v.description,
          restaurant_name: v.restaurant_name,
          currently_working: v.currently_working,
          address: v.address,
        };
      })
    : [];
});
interface FieldOptionsModalViewType extends Omit<FieldOptionsViewType, 'id' | 'selected'> {
  id: keyof ExperienceInputProps;
  selected: string | string[];
}
const defaultFieldOptions: FieldOptionsModalViewType = {
  showOptionsView: false,
  options: [],
  mainTitle: '',
  subTitle: '',
  id: 'domain_interest',
  returnType: 'string',
  selected: '',
};
const defaultModalFieldOptions = {
  ...defaultFieldOptions,
  id: 'domain_interest',
} as FieldOptionsModalViewType;
const useFieldOptions = () => {
  const [fieldOptionsView, toggleFieldOptionsView] = React.useState<FieldOptionsModalViewType | null>({
    ...defaultModalFieldOptions,
  });
  return {
    fieldOptionsView,
    toggleFieldOptionsView,
  };
};
export const WorkExperienceFormContent: React.FunctionComponent<IWorkExperienceFormContentProps> = ({
  onClose,
  editPageTitle,
  editIndex,
  mode,
}) => {
  const { t } = useTranslation();
  const { fieldOptionsView, toggleFieldOptionsView } = useFieldOptions();

  const { apiSubmitHandler, loading } = useApiSubmitHandler({
    onClose,
    editIndex,
    editPageType: 'work_experience',
    mode,
  });

  const defaultValues = useAtomValue(defaultValuesAtom);
  const { data: workerFieldOptionsData } = useAtomValue(getWorkerFieldOptionsAtom);
  const { data: workerData } = useAtomValue(getWorkerAtom);
  const methods = useForm<ExperienceInputProps>({
    reValidateMode: 'onSubmit',
    defaultValues: mode === 'edit' ? defaultValues[editIndex] : {},
  });
  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = methods;
  const domainInterestId = getValues('domain_interest');
  const { occupationOptions, occupationSkillOptions, occupationSkillOptionsOriginal } = useExperienceFieldOptions({
    skillId: 'skills',
    domainInterestId,
    occupation: getValues('occupation_id'),
    setValue,
    domainInterest: workerFieldOptionsData?.domainInterest,
    isAutoSelectSkill: mode === 'add',
  });

  const onSubmit: SubmitHandler<ExperienceInputProps> = React.useCallback(
    async (params) => {
      try {
        const pastExperienceSingleUnit: Partial<WorkerPastExperience> = {
          ...params,
          ...(params.occupation_id ? { occupation_id: +params.occupation_id } : { occupation_id: undefined }),
          ...(params.to_date
            ? { to_date: !getValues('currently_working') ? toDateFormat(params.to_date, genericDateFormat) : null }
            : {}),
          ...(params.from_date ? { from_date: toDateFormat(params.from_date, genericDateFormat) } : {}),
          skills: params.skills
            ?.filter(Boolean)
            .map((v) => {
              const skill = occupationSkillOptionsOriginal?.find((v2) => v2.skill_id === +v);
              return skill
                ? {
                    id: skill?.skill_id,
                    domain_interest: skill?.occupation?.domain_interest,
                    esco_uri: skill?.occupation?.esco_uri,
                    label_it: skill?.occupation?.label_it,
                  }
                : undefined;
            })
            .filter(Boolean),
          ...(params.domain_interest
            ? {
                domain_interest_var: workerFieldOptionsData?.domainInterest?.find(
                  (v) => v.id === params.domain_interest
                ),
              }
            : {}),
          ...(params.occupation_id
            ? {
                occupation: {
                  id: +params.occupation_id,
                  label_it: {
                    label: occupationOptions?.find((v) => v.id === params.occupation_id)?.label,
                  },
                } as Occupation,
              }
            : {}),
        };
        if (mode === 'add') {
          const inputProps: Partial<Worker> = {
            past_experiences: [
              ...workerData['past_experiences'],
              ...[pastExperienceSingleUnit as WorkerPastExperience],
            ],
          };
          apiSubmitHandler(inputProps);
        }
        if (mode === 'edit') {
          const pastExperiences = [...workerData['past_experiences']] as WorkerPastExperience[];
          pastExperiences[editIndex] = {
            ...pastExperiences[editIndex],
            ...pastExperienceSingleUnit,
          } as WorkerPastExperience;
          apiSubmitHandler({ past_experiences: pastExperiences });
        }
      } catch (error) {
        console.error(error, `Error in work experience form submit`);
      }
    },
    [
      apiSubmitHandler,
      editIndex,
      getValues,
      mode,
      occupationOptions,
      occupationSkillOptionsOriginal,
      workerData,
      workerFieldOptionsData?.domainInterest,
    ]
  );

  const domainInterestInput = React.useMemo(() => {
    return (
      <FormLabelWrapper id="domain_interest" label={t('common:sector')} isFullWidth>
        <Controller
          name="domain_interest"
          control={control}
          rules={{ required: typeof editIndex !== 'number' }}
          render={({ field }) => (
            <TextInput
              id="domain_interest"
              placeholder={t('onboarding-page:choose_sector')}
              type="text"
              rightIcon={ChevronDownIcon}
              helperText={Object.keys(errors).includes('domain_interest') ? <HelperTextRElement helperText="" /> : null}
              color={Object.keys(errors).includes('domain_interest') ? 'failure' : undefined}
              readOnly
              {...field}
              value={workerFieldOptionsData?.domainInterest?.find((v) => v.id === field.value)?.label || ''}
              onClick={() => {
                toggleFieldOptionsView({
                  showOptionsView: true,
                  id: 'domain_interest',
                  selected: field.value ?? '',
                  options: workerFieldOptionsData?.domainInterest,
                  mainTitle: t('common:sector'),
                });
              }}
            />
          )}
        />
      </FormLabelWrapper>
    );
  }, [control, errors, editIndex, toggleFieldOptionsView, workerFieldOptionsData, t]);

  const restaurantTypeInput = React.useMemo(() => {
    return (
      <FormLabelWrapper id="restaurant_type" label={t('onboarding-page:type_of_venue')} isFullWidth>
        <Controller
          name="restaurant_type"
          control={control}
          rules={{ required: typeof editIndex !== 'number' }}
          render={({ field }) => (
            <TextInput
              id="restaurant_type"
              placeholder={t('onboarding-page:choose_type_of_venue')}
              type="text"
              rightIcon={ChevronDownIcon}
              helperText={Object.keys(errors).includes('restaurant_type') ? <HelperTextRElement helperText="" /> : null}
              color={Object.keys(errors).includes('restaurant_type') ? 'failure' : undefined}
              readOnly
              {...field}
              value={workerFieldOptionsData?.restaurantType?.find((v) => v.id === field.value)?.label || ''}
              onClick={() => {
                toggleFieldOptionsView({
                  showOptionsView: true,
                  id: 'restaurant_type',
                  selected: field.value ?? '',
                  options: workerFieldOptionsData?.restaurantType,
                  mainTitle: t('onboarding-page:type_of_venue'),
                });
              }}
              disabled={!domainInterestId}
            />
          )}
        />
      </FormLabelWrapper>
    );
  }, [t, control, editIndex, errors, workerFieldOptionsData?.restaurantType, domainInterestId, toggleFieldOptionsView]);

  const occupationIdInput = React.useMemo(() => {
    return (
      <FormLabelWrapper id="occupation_id" label={t('common:role')}>
        <Controller
          name="occupation_id"
          control={control}
          rules={{ required: typeof editIndex !== 'number' }}
          render={({ field }) => (
            <TextInput
              id="occupation_id"
              placeholder={t('onboarding-page:enter_your_role')}
              type="text"
              rightIcon={ChevronDownIcon}
              helperText={Object.keys(errors).includes('occupation_id') ? <HelperTextRElement helperText="" /> : null}
              color={Object.keys(errors).includes('occupation_id') ? 'failure' : undefined}
              readOnly
              {...field}
              value={occupationOptions?.find((v) => v.id === field.value)?.label || ''}
              onClick={() => {
                toggleFieldOptionsView({
                  showOptionsView: true,
                  id: 'occupation_id',
                  selected: field.value ?? '',
                  options: occupationOptions,
                  mainTitle: t('common:role'),
                });
              }}
              disabled={!domainInterestId}
            />
          )}
        />
      </FormLabelWrapper>
    );
  }, [t, control, editIndex, errors, occupationOptions, domainInterestId, toggleFieldOptionsView]);

  const jobIntervalEl = (
    <div className="flex flex-col gap-2">
      <FormLabelWrapper
        disabled={!domainInterestId}
        id={'job_interval'}
        label={t('onboarding-page:in_what_period_did_you_work')}
      >
        <div className="flex flex-col gap-2 lg:flex-row">
          <Controller
            name="from_date"
            control={control}
            rules={{ required: false }}
            render={({ field }) => {
              return (
                <DatePicker
                  helperText={
                    Object.keys(errors).includes('from_date') ? t('onboarding-page:this_field_is_required') : null
                  }
                  fieldState={Object.keys(errors).includes('from_date') ? 'error' : 'normal'}
                  id="from_date"
                  disabled={!domainInterestId}
                  selectRange={false}
                  locale="it-IT"
                  maxDate={new Date()}
                  {...field}
                  {...(field.value ? { value: new Date(field.value) } : {})}
                />
              );
            }}
          />
          <Controller
            name="to_date"
            control={control}
            rules={{ required: false }}
            render={({ field }) => {
              return (
                <DatePicker
                  helperText={
                    Object.keys(errors).includes('to_date') ? t('onboarding-page:this_field_is_required') : null
                  }
                  fieldState={Object.keys(errors).includes('to_date') ? 'error' : 'normal'}
                  id="to_date"
                  disabled={!domainInterestId}
                  selectRange={false}
                  locale="it-IT"
                  disableCalendar={watch('currently_working')}
                  maxDate={new Date()}
                  {...field}
                  {...(field.value ? { value: new Date(field.value) } : {})}
                />
              );
            }}
          />
        </div>
      </FormLabelWrapper>
      <Controller
        control={control}
        name="currently_working"
        rules={{ required: false }}
        render={({ field }) => {
          return (
            <Checkbox
              title={t('onboarding-page:this_is_my_work_currently_in_progress')}
              disabled={!domainInterestId}
              {...{
                ...field,
                onChange: (e) => {
                  field.onChange(e.target.checked);
                },
                value: field.value?.toString(),
                checked: field.value,
              }}
            />
          );
        }}
      />
    </div>
  );

  const jobDescriptionEl = React.useMemo(() => {
    return (
      <FormLabelWrapper
        id="description"
        label={<FormLabelLabel label={t('onboarding-page:brief_job_description')} isOptional />}
      >
        <Controller
          name="description"
          control={control}
          rules={{ required: false }}
          render={({ field }) => {
            return (
              <TextArea
                disabled={!domainInterestId}
                id="description"
                placeholder={t('onboarding-page:briefly_describe_what_your_role')}
                {...field}
              />
            );
          }}
        />
      </FormLabelWrapper>
    );
  }, [control, domainInterestId, t]);

  const skillsEl = React.useMemo(() => {
    return occupationSkillOptions?.length ? (
      <FormLabelWrapper id="skills" label={t('onboarding-page:skills_acquired')}>
        <Controller
          key="skills"
          name="skills"
          rules={{ required: false }}
          control={control}
          render={({ field }) => {
            return (
              <>
                <CheckboxGroup
                  key="skills"
                  id="skills"
                  options={occupationSkillOptions ?? []}
                  mode="clickable"
                  variant="chip"
                  isWrapList={false}
                  {...{
                    ...field,
                    onChange: (e) => {
                      let checkV: string[] = field.value ? [...field.value] : [];
                      checkV =
                        !checkV.includes(e.target.id) && e.target.checked
                          ? [...checkV, e.target.id]
                          : checkV.filter((v) => v !== e.target.id);
                      field.onChange(checkV.length ? checkV : []);
                    },
                  }}
                />
              </>
            );
          }}
        />
      </FormLabelWrapper>
    ) : null;
  }, [t, control, occupationSkillOptions]);

  const handleChange = React.useCallback(
    (value: string | React.ChangeEvent<HTMLInputElement>) => {
      if (typeof value === 'string') {
        toggleFieldOptionsView((p) => ({ ...p, selected: value }));
      } else {
        const values = fieldOptionsView.selected ?? [];
        let checkV: string[] = values ? [...values] : [];
        checkV =
          !checkV.includes(value.target.id) && value.target.checked
            ? [...checkV, value.target.id]
            : checkV.filter((v) => v !== value.target.id);
        toggleFieldOptionsView((p) => ({ ...p, selected: checkV }));
      }
    },
    [fieldOptionsView.selected, toggleFieldOptionsView]
  );

  const handleSave = React.useCallback(() => {
    setValue(fieldOptionsView.id, fieldOptionsView.selected);
    toggleFieldOptionsView(defaultModalFieldOptions);
  }, [fieldOptionsView.id, fieldOptionsView.selected, setValue, toggleFieldOptionsView]);

  return (
    <>
      <div className="w-full">
        {editPageTitle ? (
          <EditModalTitle
            editPageTitle={editPageTitle}
            onClose={onClose}
            onDelete={
              mode === 'edit'
                ? () => {
                    const inputProps: Partial<Worker> = {
                      past_experiences: [...workerData['past_experiences']].filter((_, i) => i !== editIndex),
                    };
                    apiSubmitHandler(inputProps);
                  }
                : null
            }
            loading={loading}
          />
        ) : null}
        <FormProvider {...methods}>
          <form
            action=""
            autoComplete="off"
            method="post"
            onSubmit={handleSubmit(onSubmit, (validationErrors) => {
              const firstErrorField = Object.keys(validationErrors)[0];
              if (firstErrorField) {
                document.getElementById(firstErrorField)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            })}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-4 md:flex-row">
              {domainInterestInput}
              {restaurantTypeInput}
            </div>
            {occupationIdInput}
            <AutoCompleteFields />
            {jobIntervalEl}
            {jobDescriptionEl}
            {skillsEl}
            <EditModalFooter
              loading={loading}
              onClose={() => {
                onClose();
                reset(defaultValues[editIndex]);
              }}
              editIndex={editIndex}
              editPageType="work_experience"
            />
          </form>
          <DevTool control={control} />
        </FormProvider>
      </div>
      <EditFieldOptionsModal
        {...fieldOptionsView}
        value={fieldOptionsView.selected ?? (getValues(fieldOptionsView.id) as string[] | string)}
        onChange={handleChange}
        onBack={() => toggleFieldOptionsView(defaultModalFieldOptions)}
        onSave={handleSave}
        visible={fieldOptionsView.showOptionsView}
        returnType="string"
      />
    </>
  );
};

export default WorkExperienceFormContent;
const AutoCompleteFields = () => {
  const { t } = useTranslation();
  const {
    getValues,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useFormContext<ExperienceInputProps>();
  const domainInterestId = getValues('domain_interest');
  const registerAddressDataToForm = React.useCallback(
    // placeId geocodes the exact selected place: same billing as free-text, but no
    // ambiguity for similarly-named venues (chains).
    async ({ description, placeId }: { description: string; placeId?: string }) => {
      const addressData = await getLocationAddressFields(description, placeId, 'worker_profile_experience_address');
      setValue('address_lat', addressData.address_lat);
      setValue('address_lon', addressData.address_lon);
      setValue('country', addressData.country);
      setValue('administrative_area_level_1', addressData.administrative_area_level_1);
      setValue('administrative_area_level_2', addressData.administrative_area_level_2);
      setValue('postal_code', addressData.postal_code);
      setValue('city', addressData.locality);
    },
    [setValue]
  );

  const onSuggestionSelect = React.useCallback(
    async (placesData: GooglePlacePrediction) => {
      const { place_id, description, structured_formatting: descriptionObject } = placesData;
      setValue('restaurant_name', descriptionObject?.main_text);
      setValue('gmaps_place_id', place_id);
      setValue('gmaps_last_updated_at', new Date().toISOString());

      setValue('address', descriptionObject.secondary_text);
      registerAddressDataToForm({ description, placeId: place_id });
    },
    [registerAddressDataToForm, setValue]
  );

  return (
    <>
      <Controller
        name="restaurant_name"
        control={control}
        rules={{ required: true }}
        render={({ field }) => {
          return (
            <GoogleAutoCompleteInput
              mainLabel={`${t('onboarding-page:name_of_the_venue')}*`}
              formId="restaurant_name"
              key="restaurant_name"
              placeholder={t('onboarding-page:name_of_the_venue')}
              disabled={!domainInterestId}
              placesFilterTypes={['establishment']}
              trackingSurface="worker_profile_experience_venue"
              onSuggestionSelect={onSuggestionSelect}
              title={t('onboarding-page:dont_see_the_place_among_the_options')}
              subTitle={t('onboarding-page:fill_in_the_name_and_then_add_the_address')}
              helperText={errors.restaurant_name ? t('onboarding-page:this_field_is_required') : undefined}
              formValue={field.value || ''} // Ensure the input is controlled
              inputProps={{
                onChange: (value) => {
                  field.onChange(value);
                },
                color: errors.restaurant_name ? 'failure' : undefined,
              }}
            />
          );
        }}
      />
      <Controller
        name="address"
        control={control}
        rules={{ required: false }}
        render={({ field }) => {
          return (
            <GoogleAutoCompleteInput
              mainLabel={t('common:address')}
              formId="address"
              key="address"
              placeholder="Via/Piazza"
              disabled={Boolean(!domainInterestId || !watch('restaurant_name'))}
              placesFilterTypes={['address']}
              trackingSurface="worker_profile_experience_address"
              onSuggestionSelect={async (placesData) => {
                const { description, structured_formatting: descriptionObject } = placesData;
                setValue('address', descriptionObject?.main_text);
                registerAddressDataToForm({ description, placeId: placesData.place_id });
              }}
              title={t('onboarding-page:cant_find_the_correct_address')}
              subTitle={t('onboarding-page:fill_it_in_manually_or_choose_the_closest_one')}
              formValue={field.value || ''} // Ensure the input is controlled
              inputProps={{
                onChange: (value) => {
                  field.onChange(value);
                },
              }}
            />
          );
        }}
      />
    </>
  );
};
